import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { _cancelEnrollment, setParticipatedByID } from '@adornis/buchungsheld-db/api/participation.js';
import { bhBaseQlContext } from '@adornis/buchungsheld-db/bh-context.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { RegistrationData } from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { notifyAbmeldungDurchEinrichtung } from '@adornis/buchungsheld-db/notification/abmeldung-durch-einrichtung.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { notifyNuzterSperrungAufgehoben } from '@adornis/buchungsheld-db/notification/nutzer-sperrung-aufgehoben.js';
import { notifyNuzterSperrung } from '@adornis/buchungsheld-db/notification/nutzer-sperrung.js';
import { notifyVeranstaltungseintragungDurchAdmin } from '@adornis/buchungsheld-db/notification/veranstaltungseintragung-durch-admin.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { nachrueckLogic } from '../server/waitinglist-monitoring.js';

export async function _updateParticipationStateAsAdminUser(
  participations: NarrowedEntity<
    Participation,
    {
      _id: 1;
      _class: 1;
      state: 1;
      userID: 1;
      productID: 1;
      updateParticipationsStateAsAdminUser: 1;
      lastStateChangeWasInitiatedByAdmin: 1;
      timeslotID: 1;
      waitingListPlace: 1;
      nachrueckPriority: 1;
      product: {
        _id: 1;
        institutionID: 1;
        settings: { blockSignOffDaysInAdvance: 1; inheritedBlockSignOffDaysInAdvance: 1 };
      };
    }
  >[],
  nextState: ParticipationState,
) {
  if (participations.length === 0) return 0;

  const productID = participations[0]!.productID;
  if (!productID) throw new Error('productID is not defined');

  for (const participation of participations) {
    if (participation.productID !== productID)
      throw new Error(
        'all participations must be from the same product. Got ' + productID + ' and ' + participation.productID,
      );
  }

  let doNachrueckLogicAtTheEnd = false;
  await Promise.all(
    participations.map(async participation => {
      const currentState = participation.state;
      if (nextState === currentState) return;

      const institutionID = participation.product!.institutionID;
      if (!institutionID) {
        logger.error({ participation }, 'Participation has no institutionID');
        throw new Error('Participation has no institutionID');
      }

      const user = await getByID(
        User,
        participation.userID,
      )({
        _id: 1,
        email: 1,
        title: 1,
        username: 1,
        firstName: 1,
        lastName: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        suspentions: { _id: 1, institutionID: 1 },
        whatsappPhone: 1,
      });
      if (!user) throw new Error('user not found ' + participation.userID);

      const match = (inp1, inp2) => {
        const inp1Matches = inp1 === currentState || (Array.isArray(inp1) && inp1.includes(currentState));
        const inp2Matches = inp2 === nextState || (Array.isArray(inp2) && inp2.includes(nextState));
        return inp1Matches && inp2Matches;
      };

      switch (true) {
        // participated
        case match(ParticipationState.LEFT, ParticipationState.PARTICIPATED):
        case match(ParticipationState.IN, ParticipationState.PARTICIPATED):
        case match(ParticipationState.SUSPENDED, ParticipationState.PARTICIPATED):
          await setParticipatedByID(participation._id, true);
          break;

        case match(ParticipationState.WAITING, ParticipationState.PARTICIPATED):
          await setParticipatedByID(participation._id, true);
          doNachrueckLogicAtTheEnd = true;
          break;

        // not participated
        case match(ParticipationState.PARTICIPATED, ParticipationState.IN):
          await setParticipatedByID(participation._id, false);
          break;

        // waitinglist nachruecken
        case match(ParticipationState.WAITING, ParticipationState.IN):
          bhBaseQlContext.ignoreCapacity = true;
          await RegistrationData.moveUpFromWaitinglistIntoParticipating(participation._id, null)({});
          doNachrueckLogicAtTheEnd = true;
          break;

        // reparticipate
        case match(ParticipationState.LEFT, ParticipationState.IN):
          //! overlapping time check gets ignored right now
          await notifyVeranstaltungseintragungDurchAdmin(participation._id);
          break;

        // back to warteliste
        case match(ParticipationState.LEFT, ParticipationState.WAITING):
          //! overlapping time check gets ignored right now
          await notifyBestaetigungWorkshopRegistrierung(institutionID, user, [participation]);
          doNachrueckLogicAtTheEnd = true;
          break;

        // abgemeldet
        case match(ParticipationState.PARTICIPATED, ParticipationState.LEFT):
        case match(ParticipationState.IN, ParticipationState.LEFT):
          await _cancelEnrollment(participation, true);
          doNachrueckLogicAtTheEnd = true;
          break;

        // abhemeldet 2
        case match(ParticipationState.PARTICIPATED, ParticipationState.WAITING):
        case match(ParticipationState.IN, ParticipationState.WAITING):
          doNachrueckLogicAtTheEnd = true;
          break;

        // leaving the waitinglist
        case match([ParticipationState.WAITING], ParticipationState.LEFT):
          await notifyAbmeldungDurchEinrichtung({ participationID: participation._id });
          doNachrueckLogicAtTheEnd = true;
          break;

        // ingnore these abmeldungen
        case match([ParticipationState.SUSPENDED], ParticipationState.LEFT):
          break;

        // suspension
        case match(ParticipationState.PARTICIPATED, ParticipationState.SUSPENDED):
        case match(ParticipationState.IN, ParticipationState.SUSPENDED):
          await notifyNuzterSperrung(participation._id);
          doNachrueckLogicAtTheEnd = true;
          break;

        case match(ParticipationState.WAITING, ParticipationState.SUSPENDED):
          await notifyNuzterSperrung(participation._id);
          doNachrueckLogicAtTheEnd = true;
          break;

        // suspension cancled
        case match(ParticipationState.SUSPENDED, ParticipationState.IN):
        case match(ParticipationState.SUSPENDED, ParticipationState.LEFT):
          await notifyNuzterSperrungAufgehoben(participation._id);
          break;

        case match(ParticipationState.SUSPENDED, ParticipationState.WAITING):
          await notifyNuzterSperrungAufgehoben(participation._id);
          doNachrueckLogicAtTheEnd = true;
          break;

        // ignore these suspensions
        case match(ParticipationState.LEFT, ParticipationState.SUSPENDED):
          break;

        // ingnore
        case match(ParticipationState.IN, ParticipationState.IN):
        case match(ParticipationState.LEFT, ParticipationState.LEFT):
          break;

        default:
          throw new Error('state transition not implemented: ' + currentState + ' -> ' + nextState);
      }
    }),
  );

  bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
  const collection = await getRawCollection<Participation>(Participation._collectionName);
  await collection.updateMany(
    {
      _id: { $in: participations.map(p => p._id) },
    },
    {
      $set: {
        state: nextState,
        lastStateChangeWasInitiatedByAdmin: true,
      },
    },
  );

  // check if nachrücken possible
  if (doNachrueckLogicAtTheEnd) await nachrueckLogic(productID);

  return 1;
}

export const updateParticipationsStateAsAdminUser = registerMutation({
  operationName: 'updateParticipationsStateAsAdminUser',
  type: () => Int,
  params: [
    { name: 'participationIDs', type: () => [String] },
    { name: 'state', type: () => String },
  ],
  resolve: (participationIDs: string[], nextState: ParticipationState) => async () => {
    if (participationIDs.length === 0) return 0;
    const currentUser = await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!currentUser) throw new Error('not logged in');
    if (!(currentUser as User).isAdmin) throw new Error('action not permitted');

    const participations = await getByID(
      Participation,
      participationIDs,
    )({ _id: 1, _class: 1, state: 1, userID: 1, productID: 1 });

    const productID = participations[0]!.productID;
    const product = await getByID(
      Product,
      productID,
    )({
      _id: 1,
      institutionID: 1,
      settings: { blockSignOffDaysInAdvance: 1, inheritedBlockSignOffDaysInAdvance: 1 },
    });
    if (!product) throw new Error('product not found ' + productID);
    const institutionID = product.institutionID;

    // check if all participations are from the same product
    for (const participation of participations) {
      if (participation.productID !== productID) {
        throw new Error(
          'all participations must be from the same product. Got ' + productID + ' and ' + participation.productID,
        );
      }

      // avoid fetching the same product multiple times
      participation.product = product;
    }

    // check persmissions
    for (const participation of participations) {
      const currentState = participation.state;
      if (nextState === currentState) continue;

      const match = (inp1, inp2) => {
        const inp1Matches = inp1 === currentState || (Array.isArray(inp1) && inp1.includes(currentState));
        const inp2Matches = inp2 === nextState || (Array.isArray(inp2) && inp2.includes(nextState));
        return inp1Matches && inp2Matches;
      };

      switch (true) {
        // participated
        case match(ParticipationState.WAITING, ParticipationState.PARTICIPATED):
        case match(ParticipationState.LEFT, ParticipationState.PARTICIPATED):
        case match(ParticipationState.IN, ParticipationState.PARTICIPATED):
        case match(ParticipationState.SUSPENDED, ParticipationState.PARTICIPATED):
          if (
            !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)
          )
            throw new Error('You dont have enough permission!');
          break;

        // not participated
        case match(ParticipationState.PARTICIPATED, ParticipationState.IN):
          if (
            !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID) &&
            !currentUser.hasPermission(Permission.CHANGE_PARTICIPATION_STATE, institutionID)
          )
            throw new Error('You dont have enough permission!');
          break;

        case match(ParticipationState.WAITING, ParticipationState.IN): // waitinglist nachruecken
        case match(ParticipationState.LEFT, ParticipationState.IN): // reparticipate
        case match(ParticipationState.LEFT, ParticipationState.WAITING): // back to warteliste
        case match(ParticipationState.PARTICIPATED, ParticipationState.LEFT): // abgemeldet
        case match(ParticipationState.IN, ParticipationState.LEFT):
        case match(ParticipationState.PARTICIPATED, ParticipationState.WAITING): // abhemeldet 2
        case match(ParticipationState.IN, ParticipationState.WAITING):
        case match([ParticipationState.WAITING], ParticipationState.LEFT): // leaving the waitinglist
        case match([ParticipationState.SUSPENDED], ParticipationState.LEFT): // ingnore these abmeldungen
        case match(ParticipationState.PARTICIPATED, ParticipationState.SUSPENDED): // suspension
        case match(ParticipationState.IN, ParticipationState.SUSPENDED):
        case match(ParticipationState.WAITING, ParticipationState.SUSPENDED):
        case match(ParticipationState.SUSPENDED, ParticipationState.IN): // suspension cancled
        case match(ParticipationState.SUSPENDED, ParticipationState.WAITING):
        case match(ParticipationState.SUSPENDED, ParticipationState.LEFT):
        case match(ParticipationState.LEFT, ParticipationState.SUSPENDED): // ignore these suspensions
        case match(ParticipationState.IN, ParticipationState.IN): // ingnore
        case match(ParticipationState.LEFT, ParticipationState.LEFT):
          break;

        default:
          throw new Error('state transition not implemented: ' + currentState + ' -> ' + nextState);
      }
    }

    return _updateParticipationStateAsAdminUser(participations, nextState);
  },
});
